<template>
  <basic-card>
    <template slot="header">{{ $t('accounts.accounts') }}
      <context-help help-item-code="account-card-header"/>
    </template>
    <toolbox>
      <toolbox-button :help-text="`${$t('searchQuery.exportExcel')}`" @press="handleExport">
        <icon :icon="['fal', 'file-excel']" size="lg"/>
      </toolbox-button>
    </toolbox>

    <collapsible-content>
      <template slot="header">{{ $t('searchQuery.queries') }}</template>
      <form-wrapper :form-title="`${$t('searchQuery.savedQuery')}`">
        <form-select-input :caption="`${$t('searchQuery.loadQuery')}`" v-model="accountQuery"
                           :options="accountQueryOptions"/>
        <form-text-input :caption="`${$t('searchQuery.queryName')}`" v-model="accountQueryName"/>
      </form-wrapper>
      <simple-horizontal-layout>


      </simple-horizontal-layout>

      <filter-form :filter-definition="accountQueryDefinition" v-model="query"/>
      <simple-horizontal-layout>
        <empty/>
        <empty/>
        <empty/>
        <simple-button @press="clearFilter">{{ $t('searchQuery.clearQuery') }}</simple-button>
        <simple-button @press="saveFilter">{{ $t('searchQuery.saveQuery') }}</simple-button>
        <simple-button @press="pageChanged">{{ $t('searchQuery.doQuery') }}</simple-button>
      </simple-horizontal-layout>

      <form-wrapper :form-title="`${$t('accounts.balanceDate')}`">
        <form-date-input :caption="`${$t('accounts.actualDate')}`" v-model="actualDate"/>
      </form-wrapper>
    </collapsible-content>
    <data-table
        :data="accounts"
        :configuration="accountsTableConfig"
        @focusedChanged="handleFocusedChanged"
        @add="handleAddNew"
        @delete="handleDelete"
        @pageChanged="pageChanged"
        :pagination="listPagination"
        componentId="accounts-list"
    >
      <slot/>
    </data-table>
    <!--      {{balancesToDate}}-->
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import {mapGetters} from "vuex";
import DataTable from "../components/table/DataTable";
import SimpleButton from "../components/button/SimpleButton";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import Empty from "../components/layout/Empty";
import ContextHelp from "../components/help/ContextHelp";
import FilterForm from "../components/filters/FilterForm";
import QueryUtils from "../utils/QueryUtils";
import CollapsibleContent from "../components/CollapsibleContent";
import FormSelectInput from "../components/form/FormSelectInput";
import FormTextInput from "../components/form/FormTextInput";
import FormWrapper from "../components/form/FormWrapper";
import BankingStore from "../stores/banking-store"
import SwitchButton from "../components/button/SwitchButton";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import FormDateInput from "../components/form/FormDateInput";
import Toolbox from "@/components/toolbox/Toolbox";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import handleTableExport from '@/utils/excel-utils'
import {i18n} from "@/main";

const ACCOUNT_LISTING_STORE = "accountListing"

export default {
  name: "AccountsListCard",
  components: {
    ToolboxButton,
    Toolbox,
    FormDateInput,
    FormCheckboxInput,
    SwitchButton,
    FormWrapper,
    FormTextInput,
    FormSelectInput,
    CollapsibleContent,
    FilterForm, ContextHelp, Empty, SimpleHorizontalLayout, SimpleButton, DataTable, BasicCard
  },
  props: {
    accountOwnerships: Array
  },
  data: function () {
    return {
      accountQueryName: "",
      accountQuery: null,
      query: {
        parameters: {
          internal: {operator: "in", value: "true", type: "boolean", negation: false},
          active: {operator: "in", value: "true", type: "boolean", negation: false},
        }
      },
      items: [],
      actualDate: new Date(),
      accountsTableConfig: {
        objectName: this.$t('accounts.account').toLocaleLowerCase(),
        enableAdd: true,
        allowEdit: true,
        columns: [
          {
            caption: "Id",
            datum: d => d.id.substr(d.id.length - 6)
          },
          {
            caption: this.$t('accounts.active'),
            datum: d => d.active ? this.$t("yes") : this.$t("no")
          },
          {
            caption: this.$t('accounts.bank'),
            datum: d => this.partyOptions.find(party => party.id == d.bankId)?.name ?? 'N/A'
          },
          {
            caption: this.$t('accounts.name'),
            datum: d => d.name
          },
          {
            caption: this.$t('accounts.alias'),
            datum: d => d.alias
          },
          {
            caption: this.$t('accounts.eBankingName'),
            datum: d => d.eBankingAccountNumber
          },
          {
            caption: this.$t('accounts.type'),
            datum: d => d.type
          },
          {
            caption: this.$t('accounts.currency'),
            datum: d => d.currency
          },
          {
            caption: this.$t('accounts.number'),
            datum: d => d.accountNumber
          },
          {
            caption: this.$t('accounts.lastBalanceDate'),
            datum: d => this.$formats.formatDate(this.balanceToDate(d).balanceDate, false),
            // typeof this.accountBalances(d) != 'undefined' && this.accountBalances(d).length > 0 ?
            // new Date(this.accountBalances(d)[0].balanceDate).toLocaleDateString() : "N/A",
            style: (d) => ({'text-align': 'right'})
          },
          {
            caption: this.$t('accounts.actualBalance'),
            datum: d => this.$formats.formatAmount(this.balanceToDate(d).endBalance / 100),
            exportDatum: d => this.balanceToDate(d).endBalance / 100,
            style: (d) => ({'text-align': 'right'})
          },
          {
            caption: this.$t('accounts.owner'),
            datum: d => {
              let owner = this.accountOwnerships.filter(accOwnership => accOwnership.accountId === d.id);
              if (owner.length === 0) return "N/A";
              else return owner[0].partyReference.displayName
            }
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters({
      accounts: `${ACCOUNT_LISTING_STORE}/accounts`,
      accountQueries: "accountQueries/accountQueries",
      accountBalances: 'banking/accountBalances',
      currencyOptions: "currencies/currenciesOptions",
      accountOptions: 'banking/accounts',
      parties: 'parties/parties'
    }),
    partyOptions() {
      return this.parties.map(party => ({name: party.displayName, id: party.id}))
    },
    accountQueryOptions() {
      return this.accountQueries
          .filter(query => query.object == 'account')
          .map(query => ({id: query.id, name: query.name == null ? "No-name" : query.name}))
    },
    accountQueryDefinition() {
      return QueryUtils.getByType("account", {currencies: this.currencyOptions, accounts: this.accountOptions, parties: this.partyOptions});
    },
    listPagination() {
      return this.$store.getters[`${ACCOUNT_LISTING_STORE}/accountPagination`];
    }

  },
  methods: {
    async handleExport() {
      let resultList = await this.$store.dispatch(`banking/fetchAccounts`, {
        page: 0,
        size: 5000,
        query: this.query,
        sort: this.sorting
      });
      handleTableExport(this.accountsTableConfig.columns, resultList, "account")
    },
    clearFilter() {
      this.query = {parameters: {}}
      this.pageChanged()
    },
    balanceToDate(acc) {
      return this.accountBalances(acc)?.find(balance => {
        return this.$moment(balance.balanceDate).isSameOrBefore(this.actualDate, 'day')
      }) ?? "0"
    },
    saveFilter() {
      let toSave = this.$_.cloneDeep(this.query);
      if (this.accountQueryName == "") {
        return;
      }
      toSave.name = this.accountQueryName;
      toSave.object = "account"
      this.$store.dispatch("accountQueries/saveAccountQuery", toSave)
          .then(() => {
            this.$notify({
              group: 'application',
              title: `Query ${toSave.name} saved`,
              text: `Query ${this.accountQueryName} was successfully saved`,
              type: "ok"
            });
          });
    },
    pageChanged(page) {
      this.$store.dispatch(`${ACCOUNT_LISTING_STORE}/refreshAccountsPaged`, {page, query: this.query})
          .then(() => this.loading = false);
    },
    handleFocusedChanged(focused) {
      this.$emit("focusedChanged", focused);
    },
    handleAddNew() {
      this.items = [];
      this.$emit("focusedChanged", {id: null, name: null, currency: null, internal: false})
    },
    handleDelete(item) {
      this.$emit("delete", item);
    },
  },
  watch: {
    accountQuery: {
      handler(newVal) {
        let loadedQuery = this.$store.getters[`accountQueries/queryById`](newVal);
        this.query = loadedQuery;
        this.accountQueryName = loadedQuery.name;
        this.pageChanged();
      }
    },
  },
  created() {
    if (this.$store._modules.root._children[ACCOUNT_LISTING_STORE] != undefined) {
      this.$store.unregisterModule(ACCOUNT_LISTING_STORE);
    }
    this.$store.registerModule(ACCOUNT_LISTING_STORE,
        BankingStore());
    this.$store.dispatch(`${ACCOUNT_LISTING_STORE}/refreshAccountsPaged`, {page: 0, size: 150, query: this.query})
        .then(() => this.loading = false);
    this.$store.dispatch(`accountQueries/refreshAccountQueries`)
  }
}
</script>

<style lang="scss" scoped>

</style>
